import Inventory, {ApiInventory, DetailInventoryFilters, InventoryFilters} from '@/model/Inventory';
import {ApiInventoryPosition, ApiInventoryPositionReduced} from '@/model/InventoryPosition';
import {CombinedApiResponse, doApiRequest, Method, Module} from '..';
import {parseGenericFilters} from '../meta/filters';
import {ApiSortsDerived} from '../meta/odata';
import {ApiSuccessResponse} from '../types';

export enum ResourceInventur {
  Inventur = '/Inventur',
  InventurDelete = '/Inventur/action/positionenloeschen',
}

export interface ApiInventoryDeleteDatum {
  BelegNr: number;
  PosNr: number;
}

export type ApiInventoryDeleteData = ApiInventoryDeleteDatum[];

export interface ApiInventorySubmitData {
  BelegNr: number;
  Datum: string;
  Bediener: string;
  Niederlassung: number;
  Positionen: ApiInventoryPositionReduced[];
}

export async function getInventories(
    filters: InventoryFilters, sorts: ApiSortsDerived<Inventory>,
    limit: number | null = null, skip: number = 0,
): Promise<CombinedApiResponse<Inventory[]>> {
  const parsedFilters = parseGenericFilters(filters);
  const response = await doApiRequest<ApiInventory[]>({
    method: Method.Get,
    module: Module.Inventur,
    resource: ResourceInventur.Inventur,
    skip,
    limit,
    sorts,
    filters: parsedFilters,
  });

  return [response.Data.map((i) => Inventory.fromApi(i)), response.CountAll];
}

export async function getDetailInventory(
    filters: DetailInventoryFilters,
): Promise<CombinedApiResponse<Inventory|null>> {
  const parsedFilters = parseGenericFilters(filters);
  const [inventoryResponse, positionsResponse] = await Promise.all([
    doApiRequest({
      method: Method.Get,
      module: Module.Inventur,
      resource: ResourceInventur.Inventur,
      limit: 1,
      filters: parsedFilters,
    }),
    (async (): Promise<ApiSuccessResponse<ApiInventoryPosition[]>> => {
      if (!filters.Belegnr) {
        return {
          RequestId: '',
          Data: [],
          Count: 0,
          CountAll: 0,
        };
      }
      return doApiRequest({
        method: Method.Get,
        module: Module.Inventur,
        resource: ResourceInventur.Inventur,
        select: new Set(['*']),
        expand: {
          Gebinde: null,
        },
        id: filters.Belegnr,
      });
    })(),
  ]);

  return inventoryResponse.Data ? [Inventory.fromApi({...inventoryResponse.Data[0], Positionen: positionsResponse.Data})] : [null];
}

export async function postInventory(
    data: ApiInventorySubmitData,
): Promise<CombinedApiResponse<any>> {
  const response = await doApiRequest({
    module: Module.Inventur,
    resource: ResourceInventur.Inventur,
    method: Method.Post,
    data,
  });

  return response.Data ? [response.Data] : [null];
}

export async function patchInventory(
    data: ApiInventorySubmitData,
): Promise<CombinedApiResponse<any>> {
  const response = await doApiRequest({
    module: Module.Inventur,
    resource: ResourceInventur.Inventur,
    method: Method.Patch,
    data,
  });

  return response.Data ? [response.Data] : [null];
}

export async function deleteInventory(
    data: ApiInventoryDeleteData,
): Promise<CombinedApiResponse<any>> {
  const response = await doApiRequest({
    module: Module.Inventur,
    resource: ResourceInventur.InventurDelete,
    method: Method.Post,
    data,
  });

  return response.Data ? [response.Data] : [null];
}
